import React, { useMemo } from 'react';
import { BarChart } from '../../charts/bar-chart';
import { Bar } from '../../charts/bar';
import { Grid } from '../../charts/grid';
import { BarXAxis } from '../../charts/bar-x-axis';
import { YAxis } from '../../charts/y-axis';
import { ChartTooltip } from '../../charts/tooltip';
import { getFormatter } from '../formatters';
import {
  useIsDarkMode,
  compactFormat,
  seriesColor,
  isNumeric,
  labelColor,
  RnPointLabels,
} from './chartTheme';
import { isInkShell } from '../../../styles/chartColors';
import { ChartLegend } from './ChartLegend';
import { prefersReducedMotion } from '../AnimatedValue';
import '../ReportsNative.css';

/**
 * Grouped (or stacked) column chart — rounded 3px column tops, orange
 * primary series, onyx secondary (per revenue-projections / forecast
 * "Month-End Projection by Week"). Rendered with the vendored bklit
 * BarChart (band x scale — category strings used directly).
 *
 * Props:
 *   data, xKey, series [{ key, label, color?, stack?, fill? }]
 *             fill may be a per-datum resolver (point, index) => color, for
 *             semantic colouring — signed variance bars, forecast vs actual
 *             months — where one series carries more than one meaning.
 *   labels    value labels above columns (default false)
 *   yFormat   (default 'currencyCompact'), currency
 *   height    (default 240), legend (default true), barSize, yDomain
 *
 * barSize / yDomain are kept for API compatibility: the kit sizes bars from
 * the band scale (no max-width cap) and derives the y domain from the data.
 */
export function GroupedColumnChart({
  data = [],
  xKey,
  series = [],
  labels = false,
  yFormat = 'currencyCompact',
  currency,
  height = 240,
  legend = true,
  // 'peak' (default) labels only the top of the scale — the high-water
  // mark. 'full' restores the tick ladder for charts read for values
  // rather than shape.
  yAxis = 'peak',
  barSize, // eslint-disable-line no-unused-vars -- kit bars size from the band scale
  yDomain, // eslint-disable-line no-unused-vars -- kit derives domain from data
  margin,
  loading = false,
}) {
  const dark = useIsDarkMode();
  const fmt = getFormatter(yFormat, currency ? { currency } : undefined);
  const tickFmt = getFormatter(compactFormat(yFormat), currency ? { currency } : undefined);
  const stacked = series.some((s) => s.stack);

  const rows = useMemo(() => {
    // Coerce numeric strings — the kit skips any non-`number` bar value.
    return data.map((d) => {
      const row = { ...d };
      for (const s of series) {
        const v = row[s.key];
        if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) row[s.key] = Number(v);
      }
      return row;
    });
  }, [data, series]);

  const base = margin || { top: labels ? 22 : 10, right: 16, bottom: 0, left: 0 };
  const kitMargin = {
    top: base.top,
    right: base.right,
    bottom: base.bottom + (isInkShell() ? 24 : 26),
    left: base.left + (isInkShell() ? 46 : 58),
  };

  const labelSeries = labels
    ? series.filter((s, i) => !s.stack || i === series.length - 1)
    : [];

  return (
    <div>
      {legend && <ChartLegend series={series} />}
      <div className="rn-chart-body" style={{ height }}>
        <BarChart
          status={loading ? 'loading' : 'ready'}
          className="rn-bklit"
          data={rows}
          xDataKey={xKey}
          margin={kitMargin}
          aspectRatio={null}
          style={{ height: '100%' }}
          stacked={stacked}
          animationDuration={prefersReducedMotion() ? 0 : undefined}
        >
          <Grid horizontal numTicksRows={4} />
          <BarXAxis />
          <YAxis peak={yAxis === 'peak'} numTicks={4} formatValue={(v) => tickFmt(v)} />
          <ChartTooltip
            title={null}
            rows={(point) =>
              series.map((s, i) => ({
                color: seriesColor(s, i, dark),
                label: s.label || s.key,
                value: fmt(point[s.key]),
              }))
            }
          />
          {series.map((s, i) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              fill={s.fill ?? seriesColor(s, i, dark)}
              lineCap={3}
              groupGap={3}
            />
          ))}
          {labelSeries.map((s) => (
            <RnPointLabels
              key={`labels-${s.key}`}
              k={s.key}
              formatter={(v) => (isNumeric(v) ? fmt(v) : '')}
              fill={labelColor(dark)}
              fontSize={10}
            />
          ))}
        </BarChart>
      </div>
    </div>
  );
}

export default GroupedColumnChart;
